import { Icon, Stack, spacing, Text, Tooltip } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import { clampBetween0And1, formatPercentage, formatValueWithUnit } from '../../../utils/format';
import { WidgetActionButton } from '../../widget-actions';
import {
  FoldedChildrenButton,
  FoldedChildrenSlot,
  LabelSlot,
  LabelText,
  NodeBox,
  NodeValue,
} from './NestedProportionBar.styles';
import type { BoxOptions, NodePath, ProportionNode } from './NestedProportionBar.types';
import { childWidthShares, countDescendants, hasChildren, minBoxWidth, nodeIdOf } from './NestedProportionBar.utils';

/** Minimum height of one nesting level. */
const LEVEL_HEIGHT = spacing.f24;

/** A node's name and share; the tooltip covers both, so a box too narrow for its name still opens one. */
const NodeHeading = ({
  label,
  description,
  value,
  share,
  ratioOfTotal,
  ratioOfParent,
}: {
  label: string;
  description?: string;
  value: string;
  share: string;
  ratioOfTotal: number;
  ratioOfParent: number;
}) => (
  <LabelSlot>
    <Tooltip
      placement="top"
      overlay={
        <Stack direction="vertical" gap="r2">
          <Text variant="Smaller" isEmphazed>
            {label}
          </Text>
          {description ? (
            <Text variant="Smaller" color="textSecondary">
              {description}
            </Text>
          ) : null}
          <Text variant="Smaller" color="textSecondary">
            {value}
          </Text>
          <Text variant="Smaller" color="textSecondary">
            {`${formatPercentage(ratioOfTotal)} of the total`}
          </Text>
          <Text variant="Smaller" color="textSecondary">
            {`${formatPercentage(ratioOfParent)} of its parent`}
          </Text>
        </Stack>
      }
    >
      <Box display="flex" alignItems="center" minWidth={0}>
        {/* Sized by the room the share leaves, never by the text: what `LabelText` hides by. */}
        <Box container flex="1 1 auto">
          <LabelText>
            <Text variant="Smaller" color="textSecondary">
              {label}
            </Text>
          </LabelText>
        </Box>
        <NodeShare>{share}</NodeShare>
      </Box>
    </Tooltip>
  </LabelSlot>
);

/** The share a node prints, on the same row as its name and never squeezed by it. */
const NodeShare = ({ children }: { children: string }) => (
  <Box flex="0 0 auto">
    <NodeValue>
      <Text>{children}</Text>
    </NodeValue>
  </Box>
);

/**
 * One box of the bar: a node, as wide as its share of its parent, holding the nodes under it.
 *
 * Every value sits on its own label row — uniform and predictable, one line taller per level, and
 * it never has to fit in whatever width the children left over.
 */
export const ProportionBox = ({
  node,
  path,
  parentValue,
  widthShare,
  depth,
  options,
}: {
  node: ProportionNode;
  path: NodePath;
  parentValue: number;
  /**
   * The box's width as a share of its parent's inner width. Normally the same as its share of
   * the parent's *value* — the two come apart only when a payload's children sum past it.
   */
  widthShare: number;
  depth: number;
  options: BoxOptions;
}) => {
  const ratioOfParent = parentValue > 0 ? clampBetween0And1(node.value / parentValue) : 0;
  const ratioOfTotal = options.total > 0 ? clampBetween0And1(node.value / options.total) : 0;
  const share =
    options.figure === 'value'
      ? formatValueWithUnit(node.value, options.unit)
      : formatPercentage(options.figure === 'total' ? ratioOfTotal : ratioOfParent);

  const isFolded = options.foldedIds.has(nodeIdOf(path));
  const canZoomInto = depth > 0 && hasChildren(node);
  const zoomIntoNode = () => options.onZoomInto(path);
  const zoomLabel = `Zoom into ${node.label}`;
  const children = isFolded ? [] : (node.children ?? []);
  const childShares = childWidthShares(node);

  // Grows from 0 by its share, so flexbox can pin a box at its min width and share out the rest.
  const width =
    depth === 0
      ? { width: '100%' }
      : {
          flex: `${widthShare.toFixed(4)} 1 0%`,
          minWidth: `${minBoxWidth(node, path, options.foldedIds, options.ownMinWidth)}px`,
        };

  return (
    <Box display="flex" flexDirection="column" minWidth={0} {...width}>
      <NodeBox $color={options.colors[node.key]} role="group" aria-label={`${node.label} ${share}`} tabIndex={0}>
        <Box display="flex" alignItems="center" gap={spacing.f8} minWidth={0} minHeight={LEVEL_HEIGHT}>
          <NodeHeading
            label={node.label}
            description={node.description}
            value={formatValueWithUnit(node.value, options.unit)}
            share={share}
            ratioOfTotal={ratioOfTotal}
            ratioOfParent={ratioOfParent}
          />
          {canZoomInto && !isFolded ? (
            <WidgetActionButton icon="Search" label={zoomLabel} tooltip={zoomLabel} onClick={zoomIntoNode} />
          ) : null}
        </Box>
        {isFolded ? (
          <FoldedChildrenSlot>
            <Tooltip placement="top" overlay={zoomLabel}>
              <FoldedChildrenButton $color={options.colors[node.key]} onClick={zoomIntoNode} aria-label={zoomLabel}>
                <Icon name="Search" />
                <Text variant="Smaller">{`+${countDescendants(node)}`}</Text>
              </FoldedChildrenButton>
            </Tooltip>
          </FoldedChildrenSlot>
        ) : null}
        {children.length > 0 && (
          /* `stretch` is what makes siblings the same height: the row's cross size is set by its
             tallest child — the one with the deepest subtree — and every other child fills it.
             Without it a leaf sits at its own content height and the row reads as ragged. */
          <Box display="flex" alignItems="stretch" gap={spacing.f2} minWidth={0} minHeight={LEVEL_HEIGHT}>
            {children.map((child, index) => (
              <ProportionBox
                key={child.key}
                node={child}
                path={[...path, child.key]}
                parentValue={node.value}
                widthShare={childShares[index]}
                depth={depth + 1}
                options={options}
              />
            ))}
          </Box>
        )}
      </NodeBox>
    </Box>
  );
};
